import { from, fromEvent, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { KeyCodes } from '@stockcare-helpers/key-codes';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { Settings } from '@stockcare-helpers/settings';

export interface IDetect {
  type?: string;
  time?: number;
  event?: KeyboardEvent;
  value?: string | number;
}

export interface IDelete {
  type?: string;
  event?: KeyboardEvent;
  value?: string | number;
}

@Directive({
  standalone: false,
  selector: '[appScannerDetect]',
})
export class ScannerDetectDirective implements AfterViewInit, OnDestroy {
  private _disableKeyPress: boolean;
  private listeningKeyboard$: Subscription;
  timeToDetectAsScanner: number;

  @Input()
  set disableKeyPress(value: boolean) {
    if (this._disableKeyPress !== undefined) {
      !value ? this.initKeyboardListening() : this.clearKeyboardListening();
    }

    this._disableKeyPress = value;
  }
  get disableKeyPress(): boolean {
    return this._disableKeyPress;
  }

  private readonly _keyCodesToFilter = [KeyCodes.enter, KeyCodes.ctrl];

  private readonly _keyCodesToIgnore = [
    KeyCodes.capslock,
    KeyCodes.shift,
    KeyCodes.alt,
    KeyCodes.enter,
    //KeyCodes.ctrl,
    KeyCodes.select,
    KeyCodes.leftwindow,
    KeyCodes.leftarrow,
    KeyCodes.uparrow,
    KeyCodes.rightarrow,
    KeyCodes.downarrow,
    KeyCodes.F9,
    KeyCodes.F12,
    KeyCodes.escape,
  ];

  @HostListener('document:keyup', ['$event'])
  onKeyUp($event) {
    if (this._disableKeyPress) {
      return;
    }

    if ($event.ctrlKey) {
      return;
    }

    if ($event.code === 'Backspace' && $event.target.value === '') {
      this.onDelete.emit();
      return;
    }

    if ($event.target.nodeName == 'INPUT') {
      return;
    }

    if ($event.code === 'Backspace') {
      this.onDelete.emit();
      return;
    }

    const shouldAllowKeyPress = !this._keyCodesToIgnore.some((x) => x === $event.keyCode);

    if (!shouldAllowKeyPress) {
      return;
    }

    let event = new KeyboardEvent('keyup', { bubbles: false, key: $event.key, code: $event.code, keyCode: $event.keyCode });

    this.elementRef.nativeElement.value += $event.key;
    this.elementRef.nativeElement.dispatchEvent(event);
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown($event) {
    if (this._disableKeyPress) {
      return;
    }

    if ($event.ctrlKey) {
      return;
    }

    if ($event.target.nodeName == 'INPUT') {
      return;
    }

    const shouldAllowKeyPress = !this._keyCodesToIgnore.some((x) => x === $event.keyCode);

    if (!shouldAllowKeyPress) {
      return;
    }

    let event = new KeyboardEvent('keydown', { bubbles: false, key: $event.key, code: $event.code, keyCode: $event.keyCode });
    this.elementRef.nativeElement.dispatchEvent(event);
  }

  /**
   * Input delay
   */
  @Input() public debounce: number = 300;

  /**
   * After how many characters start search
   */
  @Input() public skipStart: number = 0;

  /**
   * Data cleansing event
   */
  @Output() public onDelete: EventEmitter<IDelete> = new EventEmitter();

  /**
   * Event after data entry
   */
  @Output() public onDetected: EventEmitter<IDetect> = new EventEmitter();

  /**
   * Use for unsubscribe
   */
  private destroy$ = new Subject();

  constructor(private elementRef: ElementRef, private settingsService: SettingsService, private deviceService: DeviceService) {
    this.timeToDetectAsScanner = this.settingsService.getSettingNumber(Settings.TIME_LIMIT_TO_DETECT_INPUT_AS_SCANNER);
  }

  public ngAfterViewInit() {
    this.initKeyboardListening();
  }

  private clearKeyboardListening(): void {
    this.listeningKeyboard$ && this.listeningKeyboard$.unsubscribe();
  }

  private initKeyboardListening(): void {
    /**
     * Often the code scanner is connected to the computer.
     * It emulates a press key, so we use keyboard events to press and release keys.
     */
    const events = ['keydown', 'keyup'];

    /**
     * Empty object for delay logic
     */
    const pressed = {};

    /**
     * Look at the
     * {@Link http://reactivex.io/documentation/operators/from.html}
     */

    this.listeningKeyboard$ = from(events)
      .pipe(
        /**
         * Look at the
         * {@Link https://rxjs-dev.firebaseapp.com/api/operators/mergeMap}
         */

        mergeMap((event) => fromEvent(this.elementRef.nativeElement, event)),

        /**
         * Prepare input data
         */
        map((event: KeyboardEvent) => {
          switch (event.type) {
            case 'keydown':
              /**
               * Since "which" is deprecated, we use it for a temporary variable
               * and set the processing time keydown.
               */
              pressed[event.keyCode] = event.timeStamp;
              break;
            case 'keyup':
              /**
               * In the delay set the difference between keydown and keyup events.
               */
              Object.assign(event, { duration: (event.timeStamp - pressed[event.keyCode]) / 1000 });
              break;
          }

          /**
           * @return {event: KeyboardEvent}
           */
          return event;
        }),
        filter((event: KeyboardEvent) => {
          const shouldFilterKeyCode = this._keyCodesToFilter.some((x) => x === event.keyCode);

          if (shouldFilterKeyCode) {
            return false;
          }

          if (
            (event.target as HTMLInputElement).nodeName !== 'INPUT' &&
            (event.keyCode === 8 || event.code === 'Backspace' || event.keyCode === 8)
          ) {
            /**
             * Used to clear data.
             */
            this.onDelete.emit({ event, value: (event.target as HTMLInputElement).value, type: 'delete' });
          }

          /**
           * Return data after typed in two characters.
           */
          return (event.target as HTMLInputElement).value.length > this.skipStart;
        }),

        /**
         * Data entry delay is used to limit requests.
         */
        debounceTime(this.debounce),

        /**
         * Look at the
         * {@Link http://reactivex.io/documentation/operators/distinct.html}
         */
        distinctUntilChanged()
      )

      /**
       * Use for unsubscribe
       */
      .pipe(takeUntil(this.destroy$))

      /**
       * Subscribe to the input data and determine the delay time for our purposes.
       */
      .subscribe((event: any) => {
        const shouldAllowKeyPress = !this._keyCodesToIgnore.some((x) => x === event.keyCode);

        if (!shouldAllowKeyPress) {
          return;
        }

        if (event.duration > this.timeToDetectAsScanner || event.duration == 0) {
          /**
           * Keyboard input.
           */
          console.log('typed ' + event.target.value + ' ' + event.duration + ' ' + this.timeToDetectAsScanner);
          this.onDetected.emit({ event, value: event.target.value, time: event.duration, type: 'keyboard' });
        } else if (event.duration <= this.timeToDetectAsScanner) {
          /**
           * Input from the scanner.
           */
          console.log('scanned ' + event.target.value + ' ' + event.duration + ' ' + this.timeToDetectAsScanner);
          this.onDetected.emit({ event, value: event.target.value, time: event.duration, type: 'scanner' });
        }
      });
  }

  /**
   * Use for unsubscribe
   */
  public ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.listeningKeyboard$.unsubscribe();
  }
}
